import {
  dateFormatYYYYMMDD,
  dateLocale,
  formatDateShortPreserveNull,
  getLocaleDateFormat,
  getMinimumFormDateValueLength
} from '@glow/common'
import classNames from 'classnames'
import { isValid, parse } from 'date-fns'
import { t } from 'i18next'
import React, {
  ChangeEvent,
  CSSProperties,
  MouseEvent,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState
} from 'react'
import { DayPicker, type DayPickerLocale } from 'react-day-picker'
import { createPortal } from 'react-dom'
import { useStableId } from '../../utils'
import { FormInput, Props as InputProps } from '../form-input'
import { IconFa } from '../icon-fa'
import { PlainButton } from '../plain-button'
import { getPositionStyles } from './dateUtils'
import dayPickerStyles from './day-picker.module.css'

export interface FormDateProps extends Omit<InputProps, 'ref'> {
  label: string
  value: string
  name?: string
  onValueChange: (value: string) => void
  onDateChange?: (value: string) => void
  lang: string
  icon?: ReactNode
  help?: string
  disabled?: boolean
  required?: boolean
  disabledDate?: (date: Date) => boolean
  fromMonth?: Date
  hideLabel?: boolean
  style?: CSSProperties
  error?: boolean
  errorMessage?: ReactNode
  descriptionText?: string
}

export function FormDate({
  onValueChange,
  fromMonth,
  disabledDate,
  onDateChange,
  onClear,
  style,
  error,
  value,
  ...props
}: FormDateProps): ReactElement {
  const labelClassName = classNames(dayPickerStyles.label)
  const locale = dateLocale(props.lang)
  const minimumDateLength = getMinimumFormDateValueLength(locale)
  const parsedDate = value.length >= minimumDateLength ? new Date(value) : undefined
  const popoverId = useStableId()
  const formElId = useId()
  const referenceElement = useRef<HTMLInputElement>(null)
  const popperElement = useRef<HTMLDialogElement>(null)
  const [menuStyles, setMenuStyles] = useState<React.CSSProperties>()
  const [month, setMonth] = useState(isValid(parsedDate) ? parsedDate : new Date())
  const [isDateValid, setIsDateValid] = useState(true)

  useEffect(() => setMonth(isValid(parsedDate) ? parsedDate : new Date()), [value])

  const showPopover = useCallback(() => {
    setMenuStyles({ opacity: 0 })
    // showModal() moves focus into the dialog, and until the effect below positions it the UA
    // stylesheet's `dialog:modal { inset-block-start: 0 }` keeps it at the top of the document,
    // so that focus scrolls the page to the top. Put the page back where the user left it.
    const { scrollX, scrollY } = window
    popperElement?.current?.showModal()
    if (window.scrollX !== scrollX || window.scrollY !== scrollY) {
      window.scrollTo(scrollX, scrollY)
    }
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: biome is wrong popperElement.current?.open must trigger this
  useLayoutEffect(() => {
    if (popperElement.current?.open && referenceElement.current) {
      const bodyRect = document.body.getBoundingClientRect()
      const inputRect = referenceElement.current.getBoundingClientRect()
      const popperRect = popperElement.current.getBoundingClientRect()
      setMenuStyles(getPositionStyles(bodyRect, inputRect, popperRect))
    }
  }, [popperElement.current?.open])

  const onSelect = (date?: Date) => {
    // When the user selects the date that is already selected `date` will be undefined. That
    // is because the date is meant to be deselected. That is not what we want however, hence
    // ignoring undefined values.
    if (date) {
      onValueChange(dateFormatYYYYMMDD(date, locale))
      onDateChange?.(dateFormatYYYYMMDD(date, locale))
    }

    setIsDateValid(true)
    onClose()
  }

  const onClose = (event?: React.SyntheticEvent) => {
    event?.stopPropagation()
    popperElement.current?.close()
    setMenuStyles(undefined)
  }

  const onClearDate = onClear
    ? () => {
        setIsDateValid(true)
        onClear()
      }
    : undefined

  const onIconClick = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    showPopover()
  }

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const current = (e.target as HTMLInputElement).value
    const currentDate = parse(current, getLocaleDateFormat(locale), new Date(), { locale })
    if (current.length >= minimumDateLength) {
      onValueChange(isValid(currentDate) ? dateFormatYYYYMMDD(currentDate, locale) : current)
    } else {
      onValueChange(current)
    }
    if (current.length >= 6) {
      setIsDateValid(isValid(currentDate))
    } else {
      setIsDateValid(true)
    }
  }

  const icon = props.icon || <IconFa icon={['far', 'calendar-alt']} />
  const isDatePickerOpen = popperElement.current?.open || false
  return (
    <div className={labelClassName} style={style}>
      <FormInput
        {...props}
        value={
          value.length >= minimumDateLength // formatDateShortPreserveNull() might try to parse the date even after 2 keystrokes, so wait till user has entered most parts of the date
            ? (formatDateShortPreserveNull(value, locale) ?? value)
            : value
        }
        id={formElId}
        label={props.label}
        hideLabel={props.hideLabel}
        ref={referenceElement}
        help={props.help}
        icon={
          <PlainButton type="button" className="w-8 h-6" onClick={onIconClick} aria-label={t('glow.openCalendar')}>
            {icon}
          </PlainButton>
        }
        disabled={props.disabled}
        aria-required={props.required}
        onChange={onChange}
        onClear={onClearDate}
        placeholder="YYYY-MM-DD"
        aria-describedby={popoverId}
        className={classNames('md:max-w-[160px]', props.className)}
        aria-label={props.label}
        aria-invalid={error || !isDateValid}
        errorMessage={props.errorMessage || (!isDateValid ? t('glow.invalidDateFormat') : null)}
        aria-expanded={isDatePickerOpen}
        aria-controls={popoverId}
        descriptionText={props.descriptionText}
      />
      {createPortal(
        <>
          {/** biome-ignore lint/a11y/useKeyWithClickEvents: as before */}
          <dialog
            ref={popperElement}
            onClose={(event) => onClose(event)}
            onClick={(e) => {
              // This magic snippet make sure the modal closes when clicked outside.
              // However, when clicking inside, e.g. on the chevron to change month, it stays open.
              return e.target === e.currentTarget && onClose()
            }}
            id={popoverId}
            aria-labelledby={formElId}
            style={menuStyles}
            className={classNames(
              'max-w-[calc(100vw-2rem)]',
              'min-w-min',
              'bg-theme-surface',
              'shadow-dark',
              'rounded-xs',
              'absolute',
              'm-0',
              'backdrop:bg-transparent'
            )}
          >
            <DayPicker
              mode="single"
              onSelect={onSelect}
              disabled={disabledDate}
              selected={parsedDate}
              month={month}
              onMonthChange={setMonth}
              startMonth={fromMonth}
              locale={locale as Partial<DayPickerLocale>}
              ISOWeek
              classNames={dayPickerStyles}
            />
          </dialog>
        </>,
        document.body
      )}
    </div>
  )
}
